import React, { useEffect, useRef, useState } from 'react';
import classnames from 'classnames';

import type { Theme } from '@material-ui/core';
import { useKlpStyles } from '@klp/design/Theme/useKlpStyles';

type Props = {
    autoHeight?: boolean;
    content?: string;
    src?: string;
    className?: string;
    'data-cy'?: string;
    minHeight?: number;
    extraMargin?: number;
    fullScreen?: boolean;
    noSandbox?: boolean;
};

const styles = (theme: Theme) => ({
    iframeClass: {
        width: '100%',
        fontFamily: 'Arial',
        border: 0,
        overflowY: 'hidden',
        paddingTop: theme.spacing(4),
        height: `calc(100vh - ${theme.header.height + theme.mainContent.paddingBottom}px ) !important`,
        '& img': {
            maxWidth: '100%',
            height: 'auto',
        },
        '& blockquote': {
            display: 'none',
        },
    },
    fullScreen: {
        height: '100%',
    },
});

export const IFrame: React.FC<Props> = ({
    autoHeight,
    content,
    src,
    className,
    fullScreen,
    noSandbox,
    minHeight = 1000000,
    extraMargin = 0,
    'data-cy': dataCy,
}) => {
    const iframeRef = useRef<HTMLIFrameElement>(null);
    const [height, setHeight] = useState<number>(100);
    const classes = useKlpStyles({}, styles);

    useEffect(() => {
        const handleMessage = (event: MessageEvent) => {
            if (iframeRef.current?.contentWindow !== event.source) {
                return;
            }

            if (event.data.type === 'MILJOPROFIL_READY') {
                iframeRef.current?.contentWindow?.postMessage(
                    {
                        type: 'MILJOPROFIL_PARENT_ID',
                        parent: 'eiendomsdashboard',
                    },
                    '*',
                );
            }
        };

        // Always listen for messages
        window.addEventListener('message', handleMessage);

        // Only set up auto-height if enabled
        let node: HTMLIFrameElement | null = null;
        let handleLoad: () => void = () => {};
        if (autoHeight && iframeRef.current) {
            handleLoad = () => {
                try {
                    const iframe = iframeRef.current;
                    if (!iframe?.contentWindow?.document?.body) return;

                    const scrollHeight =
                        iframe.contentWindow.document.body.scrollHeight;
                    setHeight(Math.min(scrollHeight + extraMargin, minHeight));
                } catch {
                    // Ignore cross-origin or access issues
                }
            };

            node = iframeRef.current;
            node.addEventListener('load', handleLoad);
        }

        return () => {
            window.removeEventListener('message', handleMessage);
            if (node) {
                node.removeEventListener('load', handleLoad);
            }
        };
    }, [autoHeight, extraMargin, minHeight]);

    const sandbox = noSandbox
        ? undefined
        : [
              'allow-same-origin',
              'allow-popups',
              'allow-scripts',
              'allow-popups-to-escape-sandbox',
              'allow-forms',
              'allow-modals',
              'allow-downloads',
          ].join(' ');

    const iframeHeight = autoHeight ? height : '100%';

    return (
        <iframe
            title="iframe"
            ref={iframeRef}
            srcDoc={content}
            src={src}
            className={classnames(classes.iframeClass, className, {
                [classes.fullScreen]: fullScreen,
            })}
            style={{ height: iframeHeight }}
            sandbox={sandbox}
            data-cy={dataCy}
        />
    );
};
